import { useLazyQuery } from '@oneaudi/onegraph-client';
import { useAtom } from 'jotai';
import { useCallback } from 'react';
import { useAppDispatch, useAppState } from '~/app/contexts/app-context';
import { FinancingData } from '~/app/contexts/reducers/app-reducer';
import { useMarketConfig } from '~/app/market-config';
import { FinancingDocument, FinancingInput } from '~/graphql/__generated__/graphql';
import { useConfigurator } from '~/graphql/hooks/use-configurator';
import { state } from '~/storage';

export function useFinancing() {
	const { config } = useMarketConfig();
	const { carIdentifier } = useConfigurator();
	const dispatch = useAppDispatch();
	const { financingData } = useAppState();
	const [financialError, setFinancialError] = useAtom(state.financialError);
	const { fetchFinancing, isLoadingFinancing } = useFinancingLazyQuery();

	const dispatchFinancing = useCallback(
		(newFinancing: FinancingData) => {
			dispatch({ type: 'SET_FINANCING_DATA', payload: newFinancing });
		},
		[dispatch],
	);

	const getFinancing = useCallback(async () => {
		if (config.enableFinancingServices) {
			dispatchFinancing({ ...(financingData || ({} as FinancingData)), loading: true });
			try {
				const result = await fetchFinancing({
					variables: {
						configuredCarIdentifier: carIdentifier,
						financingInput: config.financingInput as FinancingInput,
						enablePartsRateCalculation: config.enablePartsRateCalculation,
						enableFinancingDetails: config.enableFinancingDetails,
					},
				});
				if (result.data?.configuredCar.financing || result.data?.configuredCar.financingDetails) {
					const _financing = {
						// Only use financing when given. For CA market we don't request financing information anymore, because we take them from financingDetails
						...(result.data?.configuredCar.financing && { ...result.data?.configuredCar.financing }),
						// Currently financingDetails are only used in CA market because we need additional information which are not sent via financing
						...(result.data?.configuredCar.financingDetails && {
							meta: result.data.configuredCar.financingDetails?.meta,
							disclaimers: result.data.configuredCar.financingDetails?.disclaimers,
						}),
					};

					dispatchFinancing({
						financing: _financing,
						financingDetails: result.data?.configuredCar.financingDetails,
						loading: false,
					});
				} else {
					setFinancialError(true);
					dispatchFinancing({ ...(financingData || ({} as FinancingData)), loading: false });
				}
			} catch (error) {
				setFinancialError(true);
				dispatchFinancing({ ...(financingData || ({} as FinancingData)), loading: false });
			}
		}
	}, [
		JSON.stringify(carIdentifier),
		fetchFinancing,
		financingData?.financing,
		config.financingInput,
		dispatchFinancing,
		config.enableFinancingServices,
		setFinancialError,
		financialError,
	]);

	const partRateByPr = (prNumber: string | undefined): number | undefined => {
		if (prNumber) {
			const partRatesMap = financingData?.financing?.partRates?.reduce(
				(acc, item) => {
					if (item?.pr) acc[item.pr] = item.rate;
					return acc;
				},
				{} as Record<string, number>,
			);

			const matchingKey = Object.keys(partRatesMap || {}).find((key) => prNumber.endsWith(key));

			return matchingKey ? partRatesMap?.[matchingKey] : undefined;
		}
		return undefined;
	};

	return { getFinancing, isLoadingFinancing, partRateByPr };
}

function useFinancingLazyQuery() {
	const [fetchFinancing, { loading: isLoadingFinancing }] = useLazyQuery(FinancingDocument, {
		returnPartialData: true,
		fetchPolicy: 'no-cache',
		errorPolicy: 'ignore',
	});
	return { fetchFinancing, isLoadingFinancing };
}
